import React, { useState, useEffect } from 'react';
import AssetService from "../../services/AssetService";

import { 
  Table, 
  Button, 
  Card, 
  Steps, 
  Select, 
  Checkbox, 
  Input, 
  Space, 
  Tag, 
  Divider,
  Row,
  Col,
  Modal,
  message,
  Collapse,
  Tooltip,
  Badge,
  Radio,
  Switch,
  Spin
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined,
  DownloadOutlined,
  SaveOutlined,
  TableOutlined,
  LinkOutlined,
  FilterOutlined,
  EyeOutlined,
  SortAscendingOutlined,
  FunctionOutlined,
  LoadingOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Panel } = Collapse;
const { Step } = Steps;
const { TextArea } = Input;

const aggregateFunctions = [
  { value: 'COUNT', label: 'COUNT' },
  { value: 'SUM', label: 'SUM' },
  { value: 'AVG', label: 'AVG' },
  { value: 'MIN', label: 'MIN' },
  { value: 'MAX', label: 'MAX' }
];

const AdHocReportBuilder = () => {
  const [loading, setLoading] = useState(true);
  const [databaseSchema, setDatabaseSchema] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [tableJoins, setTableJoins] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [orderBy, setOrderBy] = useState([]);
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showSQLModal, setShowSQLModal] = useState(false);
  const [generatedSQL, setGeneratedSQL] = useState('');
  const [useDistinct, setUseDistinct] = useState(false);
  const [limitRows, setLimitRows] = useState(null);
  const [executing, setExecuting] = useState(false);

  // Load database schema on mount
  useEffect(() => {
    loadDatabaseSchema();
  }, []);

  const loadDatabaseSchema = async () => {
    try {
      setLoading(true);
      const response = await AssetService.getDatabaseSchema();
      
      if (response.data.result) {
        setDatabaseSchema(response.data.result);
        message.success('Database schema loaded successfully');
      } else {
        throw new Error('Invalid schema format');
      }
    } catch (error) {
      console.error('Error loading schema:', error);
      message.error('Failed to load database schema: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate SQL Query with validation
  const generateSQL = () => {
    // Clean data before generating SQL
    const validFilters = filters.filter(f => f.field && f.value);
    const validJoins = tableJoins.filter(j => 
      j.leftTable && j.leftColumn && 
      j.rightTable && j.rightColumn &&
      j.leftTable !== j.rightTable
    );
    const validOrderBy = orderBy.filter(o => o.field);
    const validAggregations = aggregations.filter(a => a.field && a.function);

    let sql = 'SELECT ';
    
    if (useDistinct) sql += 'DISTINCT ';

    const fieldsList = [];
    selectedFields.forEach(field => {
      const agg = validAggregations.find(a => a.field === field);
      if (agg) {
        fieldsList.push(`${agg.function}(${field}) AS ${field.replace('.', '_')}_${agg.function.toLowerCase()}`);
      } else {
        fieldsList.push(field);
      }
    });
    sql += fieldsList.join(',\n       ');

    sql += `\nFROM ${selectedTables[0]}`;

    validJoins.forEach(join => {
      sql += `\n${join.joinType} ${join.rightTable} ON ${join.leftTable}.${join.leftColumn} = ${join.rightTable}.${join.rightColumn}`;
    });

    if (validFilters.length > 0) {
      sql += '\nWHERE ';
      const conditions = validFilters.map(f => {
        if (f.operator === 'IN') {
          return `${f.field} IN (${f.value})`;
        } else if (f.operator === 'LIKE') {
          return `${f.field} LIKE '%${f.value}%'`;
        } else {
          return `${f.field} ${f.operator} '${f.value}'`;
        }
      });
      sql += conditions.join('\n  AND ');
    }

    if (groupBy.length > 0) {
      sql += `\nGROUP BY ${groupBy.join(', ')}`;
    }

    if (validOrderBy.length > 0) {
      const orderClauses = validOrderBy.map(o => `${o.field} ${o.direction}`);
      sql += `\nORDER BY ${orderClauses.join(', ')}`;
    }

    if (limitRows) {
      sql += `\nLIMIT ${limitRows}`;
    }

    sql += ';';
    return sql;
  };

  const showGeneratedSQL = () => {
    const sql = generateSQL();
    setGeneratedSQL(sql);
    setShowSQLModal(true);
  };

  // Step 1: Select Tables
  const handleTableSelection = (tableName) => {
    if (selectedTables.includes(tableName)) {
      setSelectedTables(selectedTables.filter(t => t !== tableName));
      setSelectedFields(selectedFields.filter(f => !f.startsWith(tableName + '.')));
      setAggregations(aggregations.filter(a => !a.field.startsWith(tableName + '.')));
      setTableJoins(tableJoins.filter(j => j.leftTable !== tableName && j.rightTable !== tableName));
      setGroupBy(groupBy.filter(f => !f.startsWith(tableName + '.')));
      setOrderBy(orderBy.filter(o => !o.field.startsWith(tableName + '.')));
    } else {
      setSelectedTables([...selectedTables, tableName]);
    }
  };

  // Step 2: Select Fields and Aggregations
  const handleFieldSelection = (tableName, columnName) => {
    const fieldKey = `${tableName}.${columnName}`;
    if (selectedFields.includes(fieldKey)) {
      setSelectedFields(selectedFields.filter(f => f !== fieldKey));
      setAggregations(aggregations.filter(a => a.field !== fieldKey));
      setGroupBy(groupBy.filter(f => f !== fieldKey));
    } else {
      setSelectedFields([...selectedFields, fieldKey]);
    }
  };

  const handleAggregation = (field, func) => {
    const existing = aggregations.find(a => a.field === field);
    if (existing) {
      if (func) {
        setAggregations(aggregations.map(a => 
          a.field === field ? { ...a, function: func } : a
        ));
      } else {
        setAggregations(aggregations.filter(a => a.field !== field));
      }
    } else if (func) {
      setAggregations([...aggregations, { field, function: func }]);
    }
  };

  const selectAllFields = (tableName) => {
    const tableColumns = databaseSchema[tableName].columns;
    const tableFields = tableColumns.map(col => `${tableName}.${col.name}`);
    const otherFields = selectedFields.filter(f => !f.startsWith(tableName + '.'));
    setSelectedFields([...otherFields, ...tableFields]);
  };

  // Step 3: Define Joins
  const addJoin = () => {
    if (selectedTables.length < 2) {
      message.warning('Please select at least 2 tables before adding joins');
      return;
    }

    setTableJoins([
      ...tableJoins,
      {
        id: Date.now(),
        leftTable: selectedTables[0] || '',
        leftColumn: '',
        rightTable: selectedTables[1] || '',
        rightColumn: '',
        joinType: 'INNER JOIN'
      }
    ]);
  };

  const updateJoin = (id, field, value) => {
    setTableJoins(tableJoins.map(join => 
      join.id === id ? { ...join, [field]: value } : join
    ));
  };

  const removeJoin = (id) => {
    setTableJoins(tableJoins.filter(join => join.id !== id));
  };

  // Step 4: Add Filters
  const addFilter = () => {
    setFilters([
      ...filters,
      {
        id: Date.now(),
        field: '',
        operator: '=',
        value: ''
      }
    ]);
  };

  const updateFilter = (id, field, value) => {
    const updatedFilters = filters.map(filter => 
      filter.id === id ? { ...filter, [field]: value } : filter
    );
    setFilters(updatedFilters);
  };

  const removeFilter = (id) => {
    setFilters(filters.filter(filter => filter.id !== id));
  };

  const addGroupBy = (field) => {
    if (!groupBy.includes(field)) {
      setGroupBy([...groupBy, field]);
    }
  };

  const removeGroupBy = (field) => {
    setGroupBy(groupBy.filter(f => f !== field));
  };

  const addOrderBy = () => {
    setOrderBy([
      ...orderBy,
      {
        id: Date.now(),
        field: '',
        direction: 'ASC'
      }
    ]);
  };

  const updateOrderBy = (id, field, value) => {
    setOrderBy(orderBy.map(order => 
      order.id === id ? { ...order, [field]: value } : order
    ));
  };

  const removeOrderBy = (id) => {
    setOrderBy(orderBy.filter(order => order.id !== id));
  };

  // Generate and Execute Report with AUTO-CLEANING
  const generatePreview = async () => {
    if (selectedFields.length === 0) {
      message.warning('Please select at least one field');
      return;
    }

    // AUTO-CLEAN: Remove empty/invalid entries from state BEFORE sending
    const cleanedFilters = filters.filter(f => 
      f.field && 
      f.field.trim() !== '' && 
      f.value !== null && 
      f.value !== undefined &&
      f.value.toString().trim() !== ''
    );
    
    const cleanedJoins = tableJoins.filter(j => 
      j.leftTable && 
      j.leftColumn && 
      j.rightTable && 
      j.rightColumn &&
      j.leftTable !== j.rightTable
    );

    // Validate joins exist if multiple tables selected
    if (selectedTables.length > 1 && cleanedJoins.length === 0) {
      message.error('You have multiple tables selected but no valid joins defined. Please add at least one join in Step 3.');
      setCurrentStep(2); // Go back to joins step
      return;
    }

    const cleanedOrderBy = orderBy.filter(o => o.field && o.field.trim() !== '');
    const cleanedAggregations = aggregations.filter(a => a.field && a.function);

    // Update state with cleaned data
    setFilters(cleanedFilters);
    setTableJoins(cleanedJoins);
    setOrderBy(cleanedOrderBy);
    setAggregations(cleanedAggregations);

    try {
      setExecuting(true);
      
      const config = {
        tables: selectedTables,
        fields: selectedFields,
        aggregations: cleanedAggregations,
        joins: cleanedJoins,
        filters: cleanedFilters,
        groupBy: groupBy,
        orderBy: cleanedOrderBy,
        useDistinct: useDistinct,
        limitRows: limitRows || 100
      };
      
      console.log('=== SENDING TO API ===');
      console.log('Config:', JSON.stringify(config, null, 2));
      
      const response = await AssetService.executeReport(config);
      
      if (response.data.code === 1) {
        setPreviewData(response.data.data);
        setGeneratedSQL(response.data.sql);
        setShowPreview(true);
        message.success(`Report generated successfully! ${response.data.count} records found.`);
      } else {
        throw new Error(response.data.message || 'Failed to generate report');
      }
      
    } catch (error) {
      console.error('=== ERROR ===');
      console.error('Error:', error);
      console.error('Response:', error.response);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate report';
      message.error('Error: ' + errorMessage);
    } finally {
      setExecuting(false);
    }
  };

  // Export Report
  const exportReport = async (format) => {
    try {
      const config = {
        sql: generatedSQL,
        reportName: reportName || 'Report',
        format: format
      };
      
      if (format === 'Excel') {
        await AssetService.exportToExcel(config);
        message.success('Report exported to Excel successfully!');
      } else {
        message.info(`${format} export coming soon!`);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Failed to export report');
    }
  };

  // Save Report Configuration
  const saveReport = async () => {
    if (!reportName.trim()) {
      message.warning('Please enter a report name');
      return;
    }
    
    try {
      const reportConfig = {
        name: reportName,
        description: reportDescription,
        tables: selectedTables,
        fields: selectedFields,
        aggregations: aggregations,
        joins: tableJoins,
        filters: filters,
        groupBy: groupBy,
        orderBy: orderBy,
        useDistinct: useDistinct,
        limitRows: limitRows,
        sql: generateSQL()
      };
      
      const response = await AssetService.saveReport(reportConfig);
      
      if (response.data.code === 1) {
        message.success(`Report "${reportName}" saved successfully!`);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      message.error('Failed to save report: ' + error.message);
    }
  };

  const previewColumns = selectedFields.map(field => {
    const agg = aggregations.find(a => a.field === field);
    const displayField = agg && agg.function 
      ? `${field.replace('.', '_')}_${agg.function.toLowerCase()}`
      : field;
    
    const [table, column] = field.split('.');
    const displayName = agg && agg.function
      ? `${agg.function}(${column})`
      : column;
    
    return {
      title: displayName,
      dataIndex: displayField,
      key: displayField,
      width: 150,
      ellipsis: true
    };
  });

  const steps = [
    { title: 'Select Tables', icon: <TableOutlined /> },
    { title: 'Select Fields', icon: <EyeOutlined /> },
    { title: 'Define Joins', icon: <LinkOutlined /> },
    { title: 'Filters & Options', icon: <FilterOutlined /> }
  ];

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <Spin size="large" tip="Loading database schema..." />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-head uc d-flex">
        <div className="my-auto">
          <h2>Ad-Hoc Report Builder</h2>
        </div>
        <div className="my-auto ml-auto">
          <Space>
            <Input
              placeholder="Report Name"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              style={{ width: 200 }}
            />
            <Button 
              type="default"
              icon={<EyeOutlined />}
              onClick={showGeneratedSQL}
              disabled={selectedFields.length === 0}
            >
              View SQL
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              onClick={saveReport}
            >
              Save Report
            </Button>
          </Space>
        </div>
      </div>

      <div className="page-pad">
        <Card 
          title={
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              📊 Report Configuration
            </span>
          }
          style={{ marginBottom: '24px' }}
        >
          <Steps current={currentStep} style={{ marginBottom: '24px' }}>
            {steps.map((step, index) => (
              <Step 
                key={index} 
                title={step.title} 
                icon={step.icon}
                style={{ cursor: 'pointer' }}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </Steps>

          {/* Step 1: Select Tables */}
          {currentStep === 0 && (
            <div>
              <h3>Select Database Tables</h3>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Choose the tables you want to include in your report
              </p>
              <Row gutter={[16, 16]}>
                {Object.keys(databaseSchema).map(tableName => (
                  <Col xs={24} sm={12} md={8} lg={6} key={tableName}>
                    <Card
                      hoverable
                      onClick={() => handleTableSelection(tableName)}
                      style={{
                        border: selectedTables.includes(tableName) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                        backgroundColor: selectedTables.includes(tableName) ? '#e6f7ff' : 'white',
                        height: '100%'
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                          {databaseSchema[tableName].icon}
                        </div>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
                          {databaseSchema[tableName].displayName}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>
                          {tableName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {databaseSchema[tableName].columns.length} columns
                        </div>
                        {selectedTables.includes(tableName) && (
                          <Badge status="success" text="Selected" style={{ marginTop: '8px' }} />
                        )}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
              <Divider />
              <Space>
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(1)}
                  disabled={selectedTables.length === 0}
                >
                  Next: Select Fields
                </Button>
              </Space>
            </div>
          )}

          {/* Step 2: Select Fields with Aggregations */}
          {currentStep === 1 && (
            <div>
              <h3>Select Fields and Aggregations</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                Choose columns and optionally add aggregate functions (SUM, AVG, COUNT, etc.)
              </p>
              
              <Collapse accordion>
                {selectedTables.map(tableName => (
                  <Panel 
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          {databaseSchema[tableName].icon} {databaseSchema[tableName].displayName}
                        </span>
                        <Tag color="blue">
                          {selectedFields.filter(f => f.startsWith(tableName + '.')).length} / {databaseSchema[tableName].columns.length}
                        </Tag>
                      </div>
                    }
                    key={tableName}
                  >
                    <Space style={{ marginBottom: '12px' }}>
                      <Button size="small" onClick={() => selectAllFields(tableName)}>
                        Select All
                      </Button>
                    </Space>
                    
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {databaseSchema[tableName].columns.map(column => {
                        const fieldKey = `${tableName}.${column.name}`;
                        const isSelected = selectedFields.includes(fieldKey);
                        const currentAgg = aggregations.find(a => a.field === fieldKey);
                        
                        return (
                          <div 
                            key={column.name}
                            style={{
                              padding: '8px',
                              marginBottom: '8px',
                              backgroundColor: isSelected ? '#f0f9ff' : '#fafafa',
                              border: isSelected ? '1px solid #1890ff' : '1px solid #e8e8e8',
                              borderRadius: '4px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleFieldSelection(tableName, column.name)}
                              />
                              <div style={{ flex: 1 }}>
                                <Tooltip title={`Type: ${column.type}`}>
                                  <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                    {column.displayName}
                                  </span>
                                </Tooltip>
                                <div style={{ fontSize: '11px', color: '#999' }}>
                                  {column.name} ({column.type})
                                </div>
                              </div>
                              {isSelected && column.type === 'number' && (
                                <Select
                                  placeholder="Aggregate"
                                  allowClear
                                  size="small"
                                  style={{ width: 120 }}
                                  value={currentAgg?.function}
                                  onChange={v => handleAggregation(fieldKey, v)}
                                >
                                  {aggregateFunctions.map(func => (
                                    <Option key={func.value} value={func.value}>
                                      {func.label}
                                    </Option>
                                  ))}
                                </Select>
                              )}
                              {isSelected && column.type !== 'number' && (
                                <Select
                                  placeholder="Aggregate"
                                  allowClear
                                  size="small"
                                  style={{ width: 120 }}
                                  value={currentAgg?.function}
                                  onChange={v => handleAggregation(fieldKey, v)}
                                >
                                  <Option value="COUNT">COUNT</Option>
                                </Select>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Panel>
                ))}
              </Collapse>

              <Divider />
              <Space>
                <Button onClick={() => setCurrentStep(0)}>Previous</Button>
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(2)}
                  disabled={selectedFields.length === 0}
                >
                  Next: Define Joins
                </Button>
              </Space>
            </div>
          )}

          {/* Step 3: Define Joins */}
          {currentStep === 2 && (
            <div>
              <h3>Define Table Relationships (Joins)</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                {selectedTables.length > 1 
                  ? 'Connect your tables by defining how they relate to each other' 
                  : 'Add more tables to define joins'}
              </p>
              
              {selectedTables.length > 1 && (
                <Button 
                  type="dashed" 
                  icon={<PlusOutlined />}
                  onClick={addJoin}
                  style={{ marginBottom: '16px' }}
                >
                  Add Join
                </Button>
              )}

              {tableJoins.map(join => (
                <Card key={join.id} size="small" style={{ marginBottom: '16px' }}>
                  <Row gutter={16} align="middle">
                    <Col xs={24} sm={5}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Left Table</div>
                      <Select
                        placeholder="Select table"
                        style={{ width: '100%' }}
                        value={join.leftTable}
                        onChange={v => updateJoin(join.id, 'leftTable', v)}
                      >
                        {selectedTables.map(table => (
                          <Option key={table} value={table}>
                            {databaseSchema[table].displayName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={5}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Column</div>
                      <Select
                        placeholder="Select column"
                        style={{ width: '100%' }}
                        value={join.leftColumn}
                        onChange={v => updateJoin(join.id, 'leftColumn', v)}
                        disabled={!join.leftTable}
                      >
                        {join.leftTable && databaseSchema[join.leftTable].columns.map(col => (
                          <Option key={col.name} value={col.name}>
                            {col.displayName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={3}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Join Type</div>
                      <Select
                        style={{ width: '100%' }}
                        value={join.joinType}
                        onChange={v => updateJoin(join.id, 'joinType', v)}
                      >
                        <Option value="INNER JOIN">INNER</Option>
                        <Option value="LEFT JOIN">LEFT</Option>
                        <Option value="RIGHT JOIN">RIGHT</Option>
                        <Option value="FULL JOIN">FULL</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={5}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Right Table</div>
                      <Select
                        placeholder="Select table"
                        style={{ width: '100%' }}
                        value={join.rightTable}
                        onChange={v => updateJoin(join.id, 'rightTable', v)}
                      >
                        {selectedTables
                          .filter(table => table !== join.leftTable)
                          .map(table => (
                            <Option key={table} value={table}>
                              {databaseSchema[table].displayName}
                            </Option>
                          ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={5}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Column</div>
                      <Select
                        placeholder="Select column"
                        style={{ width: '100%' }}
                        value={join.rightColumn}
                        onChange={v => updateJoin(join.id, 'rightColumn', v)}
                        disabled={!join.rightTable}
                      >
                        {join.rightTable && databaseSchema[join.rightTable].columns.map(col => (
                          <Option key={col.name} value={col.name}>
                            {col.displayName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={1}>
                      <div style={{ fontSize: '11px', color: 'transparent', marginBottom: '4px' }}>-</div>
                      <Button 
                        danger 
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeJoin(join.id)}
                      />
                    </Col>
                  </Row>
                </Card>
              ))}

              {selectedTables.length <= 1 && (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#fafafa', 
                  borderRadius: '8px',
                  border: '1px dashed #d9d9d9'
                }}>
                  <LinkOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                  <p style={{ color: '#999' }}>
                    Joins are only needed when working with multiple tables.<br />
                    Select more tables in Step 1 to define relationships.
                  </p>
                </div>
              )}

              {/* Validation Warning for Multiple Tables without Joins */}
              {selectedTables.length > 1 && tableJoins.length === 0 && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fff2e8', 
                  border: '1px solid #ffbb96',
                  borderRadius: '4px',
                  marginTop: '16px'
                }}>
                  ⚠️ <strong>Warning:</strong> You have selected {selectedTables.length} tables but haven't defined any joins. 
                  Please add at least one join before proceeding.
                </div>
              )}

              <Divider />
              <Space>
                <Button onClick={() => setCurrentStep(1)}>Previous</Button>
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(3)}
                >
                  Next: Filters & Options
                </Button>
              </Space>
            </div>
          )}

          {/* Step 4: Filters, Group By, Order By */}
          {currentStep === 3 && (
            <div>
              <Collapse defaultActiveKey={['filters', 'groupby', 'orderby', 'options']}>
                {/* Filters Panel */}
                <Panel header={<span><FilterOutlined /> Filters (WHERE Conditions)</span>} key="filters">
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />}
                    onClick={addFilter}
                    size="small"
                    style={{ marginBottom: '12px' }}
                  >
                    Add Filter
                  </Button>

                  {filters.map(filter => (
                    <Card key={filter.id} size="small" style={{ marginBottom: '8px' }}>
                      <Row gutter={16} align="middle">
                        <Col xs={24} sm={9}>
                          <Select
                            placeholder="Select Field"
                            style={{ width: '100%' }}
                            value={filter.field}
                            onChange={v => updateFilter(filter.id, 'field', v)}
                            showSearch
                          >
                            {selectedFields.map(field => {
                              const [table, column] = field.split('.');
                              return (
                                <Option key={field} value={field}>
                                  {databaseSchema[table].displayName}.{column}
                                </Option>
                              );
                            })}
                          </Select>
                        </Col>
                        <Col xs={24} sm={5}>
                          <Select
                            style={{ width: '100%' }}
                            value={filter.operator}
                            onChange={v => updateFilter(filter.id, 'operator', v)}
                          >
                            <Option value="=">=</Option>
                            <Option value="!=">!=</Option>
                            <Option value=">">{'>'}</Option>
                            <Option value="<">{'<'}</Option>
                            <Option value=">=">{'≥'}</Option>
                            <Option value="<=">{'≤'}</Option>
                            <Option value="LIKE">LIKE</Option>
                            <Option value="IN">IN</Option>
                          </Select>
                        </Col>
                        <Col xs={24} sm={9}>
                          <Input
                            placeholder="Value"
                            value={filter.value}
                            onChange={e => updateFilter(filter.id, 'value', e.target.value)}
                          />
                        </Col>
                        <Col xs={24} sm={1}>
                          <Button 
                            danger 
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeFilter(filter.id)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}

                  {filters.some(f => !f.field || !f.value) && filters.length > 0 && (
                    <div style={{ 
                      padding: '8px', 
                      backgroundColor: '#fffbe6', 
                      border: '1px solid #ffe58f',
                      borderRadius: '4px',
                      marginTop: '8px',
                      fontSize: '12px'
                    }}>
                      ℹ️ Incomplete filters will be ignored during execution
                    </div>
                  )}
                </Panel>

                {/* Group By Panel */}
                <Panel header={<span><FunctionOutlined /> Group By</span>} key="groupby">
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    Select fields to group by (required when using aggregate functions)
                  </p>
                  <div style={{ marginBottom: '12px' }}>
                    {groupBy.map(field => (
                      <Tag 
                        key={field} 
                        closable 
                        onClose={() => removeGroupBy(field)}
                        style={{ marginBottom: '8px' }}
                      >
                        {field}
                      </Tag>
                    ))}
                  </div>
                  <Select
                    placeholder="Add field to group by"
                    style={{ width: '100%' }}
                    onChange={addGroupBy}
                    value={null}
                  >
                    {selectedFields
                      .filter(f => !groupBy.includes(f))
                      .filter(f => !aggregations.find(a => a.field === f))
                      .map(field => {
                        const [table, column] = field.split('.');
                        return (
                          <Option key={field} value={field}>
                            {databaseSchema[table].displayName}.{column}
                          </Option>
                        );
                      })}
                  </Select>
                </Panel>

                {/* Order By Panel */}
                <Panel header={<span><SortAscendingOutlined /> Order By (Sorting)</span>} key="orderby">
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />}
                    onClick={addOrderBy}
                    size="small"
                    style={{ marginBottom: '12px' }}
                  >
                    Add Sort
                  </Button>

                  {orderBy.map(order => (
                    <Card key={order.id} size="small" style={{ marginBottom: '8px' }}>
                      <Row gutter={16} align="middle">
                        <Col xs={24} sm={18}>
                          <Select
                            placeholder="Select Field"
                            style={{ width: '100%' }}
                            value={order.field}
                            onChange={v => updateOrderBy(order.id, 'field', v)}
                            showSearch
                          >
                            {selectedFields.map(field => {
                              const [table, column] = field.split('.');
                              return (
                                <Option key={field} value={field}>
                                  {databaseSchema[table].displayName}.{column}
                                </Option>
                              );
                            })}
                          </Select>
                        </Col>
                        <Col xs={24} sm={5}>
                          <Radio.Group
                            value={order.direction}
                            onChange={e => updateOrderBy(order.id, 'direction', e.target.value)}
                            size="small"
                          >
                            <Radio.Button value="ASC">ASC</Radio.Button>
                            <Radio.Button value="DESC">DESC</Radio.Button>
                          </Radio.Group>
                        </Col>
                        <Col xs={24} sm={1}>
                          <Button 
                            danger 
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeOrderBy(order.id)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Panel>

                {/* Additional Options */}
                <Panel header="⚙️ Additional Options" key="options">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Use DISTINCT (remove duplicates)</span>
                      <Switch 
                        checked={useDistinct}
                        onChange={setUseDistinct}
                      />
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div>
                      <span>Limit Results</span>
                      <Input
                        type="number"
                        placeholder="No limit"
                        value={limitRows}
                        onChange={e => setLimitRows(e.target.value ? parseInt(e.target.value) : null)}
                        style={{ width: '100%', marginTop: '8px' }}
                      />
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div>
                      <span>Report Description</span>
                      <TextArea
                        rows={3}
                        placeholder="Describe what this report does..."
                        value={reportDescription}
                        onChange={e => setReportDescription(e.target.value)}
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                  </Space>
                </Panel>
              </Collapse>

              <Divider />

              {/* Validation Messages */}
              {selectedTables.length > 1 && tableJoins.filter(j => j.leftTable && j.leftColumn && j.rightTable && j.rightColumn).length === 0 && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fff2e8', 
                  border: '1px solid #ffbb96',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}>
                  ⚠️ <strong>Warning:</strong> You have selected {selectedTables.length} tables but no valid joins. 
                  Please go back to Step 3 to define table relationships.
                </div>
              )}

              <Space>
                <Button onClick={() => setCurrentStep(2)}>Previous</Button>
                <Button 
                  type="primary" 
                  icon={executing ? <LoadingOutlined /> : <PlayCircleOutlined />}
                  onClick={generatePreview}
                  loading={executing}
                  disabled={selectedTables.length > 1 && tableJoins.filter(j => j.leftTable && j.leftColumn && j.rightTable && j.rightColumn && j.leftTable !== j.rightTable).length === 0}
                >
                  {executing ? 'Executing...' : 'Execute Report'}
                </Button>
              </Space>
            </div>
          )}
        </Card>

        {/* Preview Section */}
        {showPreview && (
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📋 Report Results ({previewData.length} records)</span>
                <Space>
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={() => exportReport('Excel')}
                  >
                    Excel
                  </Button>
                </Space>
              </div>
            }
          >
            <div style={{ marginBottom: '16px' }}>
              <Space wrap>
                <Tag color="blue">{selectedTables.length} Tables</Tag>
                <Tag color="green">{selectedFields.length} Fields</Tag>
                <Tag color="purple">{aggregations.length} Aggregations</Tag>
                <Tag color="orange">{tableJoins.length} Joins</Tag>
                <Tag color="red">{filters.filter(f => f.field && f.value).length} Filters</Tag>
                <Tag color="cyan">{groupBy.length} Group By</Tag>
                <Tag color="magenta">{orderBy.filter(o => o.field).length} Order By</Tag>
              </Space>
            </div>
            
            <Table
              columns={previewColumns}
              dataSource={previewData}
              scroll={{ x: 'max-content' }}
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              rowKey={(record, index) => index}
            />
          </Card>
        )}

        {/* SQL Modal */}
        <Modal
          title="Generated SQL Query"
          open={showSQLModal}
          onCancel={() => setShowSQLModal(false)}
          width={800}
          footer={[
            <Button key="copy" onClick={() => {
              navigator.clipboard.writeText(generatedSQL);
              message.success('SQL copied to clipboard!');
            }}>
              Copy to Clipboard
            </Button>,
            <Button key="close" type="primary" onClick={() => setShowSQLModal(false)}>
              Close
            </Button>
          ]}
        >
          <div style={{ 
            backgroundColor: '#1e1e1e', 
            color: '#d4d4d4', 
            padding: '16px',
            borderRadius: '4px',
            fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            overflowX: 'auto'
          }}>
            <pre style={{ margin: 0 }}>{generatedSQL}</pre>
          </div>
          <div style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
            This query will be executed on your IMS database to fetch real data.
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AdHocReportBuilder;